import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import JobPlanningLinesView from '../pages/JobPlanningLinesView';

// Standalone /jpl URL for the "Full screen" control (opens the same view in a new
// window). Supports both the job-scoped and resource-scoped modes.
export function jplUrl({ job, task, resource, resourceName, fromDate, toDate } = {}) {
  const q = new URLSearchParams();
  if (job) q.set('job', String(job).trim());
  if (task) q.set('task', String(task).trim());
  if (resource) q.set('resource', String(resource).trim());
  if (resourceName) q.set('resourceName', String(resourceName));
  if (fromDate) q.set('from', fromDate);
  if (toDate) q.set('to', toDate);
  return `/jpl?${q.toString()}`;
}

// Large modal pop-up hosting the Entries screen. The upper-left
// "Full screen" control re-opens it as the standalone /jpl page in a new window.
// Used from the Create Invoices entry screen (job-scoped) and the Billable tab
// (resource-scoped, read-only).
export default function JplModal({ job, task, resource, resourceName, fromDate, toDate, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const openInNewWindow = () => {
    window.open(jplUrl({ job, task, resource, resourceName, fromDate, toDate }), '_blank', 'noopener');
    onClose();
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
  const panel = { background: 'var(--card-bg, #fff)', borderRadius: 10, width: '96vw', maxWidth: 1200, height: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.3)', overflow: 'hidden' };

  // Render into document.body via a portal so the fixed overlay is always
  // positioned against the viewport, whatever its ancestors' styles.
  //
  // The embedded view owns its own compact header bar (title + Full screen +
  // Move selected + Close), kept fixed while the list scrolls, so the modal
  // itself adds no separate toolbar row.
  return createPortal(
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <JobPlanningLinesView
          job={job}
          task={task}
          resource={resource}
          resourceName={resourceName}
          fromDate={fromDate}
          toDate={toDate}
          embedded
          onFullScreen={openInNewWindow}
          onClose={onClose}
        />
      </div>
    </div>,
    document.body
  );
}
